import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { postRequest } from "../api/postRequest";

type ApiResponse<T> = {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
};

export type ThemeColors = {
  gradientHero: string;
  backgroundColor   : string;
  accent: string;
  accentHover: string;
  textPrimary: string;
  textSecondary: string;
};

type ThemeContextType = {
  theme: ThemeColors | null;
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
  setTheme: (t: ThemeColors) => void; // manuel override için (opsiyonel)
};

const ThemeContext = createContext<ThemeContextType>({
  theme: null,
  loading: true,
  refresh: async () => {},
  setTheme: () => {},
});

const applyCssVars = (t: ThemeColors) => {
  const r = document.documentElement;
  r.style.setProperty("--theme-gradient-hero", t.gradientHero);
  r.style.setProperty("--theme-background-color", t.backgroundColor);
  r.style.setProperty("--theme-accent", t.accent);
  r.style.setProperty("--theme-accent-hover", t.accentHover);
  r.style.setProperty("--theme-text-primary", t.textPrimary);
  r.style.setProperty("--theme-text-secondary", t.textSecondary);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeColors | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();

  const setTheme = (t: ThemeColors) => {
    setThemeState(t);
    applyCssVars(t);
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(undefined);

      const res = await postRequest<{}, ApiResponse<{ bilgiler: string }[]>>(
        "/AnaEkranVerileri/listele",
        {}
      );

      if (res?.success && res?.data?.length) {
        const raw = JSON.parse(res.data[0].bilgiler);
        // { theme: {...} } ya da doğrudan renkler gelebilir
        const src = raw?.theme ?? raw;

        const picked: ThemeColors = {
          gradientHero: src?.gradientHero ?? "",
          backgroundColor: src?.backgroundColor ?? "",
          accent: src?.accent ?? "",
          accentHover: src?.accentHover ?? "",
          textPrimary: src?.textPrimary ?? "",
          textSecondary: src?.textSecondary ?? "",
        };

        setTheme(picked);
      } else {
        setError("Tema bulunamadı.");
      }
    } catch (e: any) {
      setError(e?.message ?? "Tema yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const value = useMemo(
    () => ({ theme, loading, error, refresh: fetchConfig, setTheme }),
    [theme, loading, error]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
